import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ResponsiveOverflowOptions {
    /** Horizontal gap between items, in px (matches the flex `gap-*` utility). */
    gap?: number;
    /** Width to reserve for the trailing overflow trigger when some items overflow, in px. */
    reservedWidth?: number;
    /** Width subtracted from the container's measured width (e.g. its horizontal padding), in px. */
    widthBuffer?: number;
    /** When false, measurement is skipped and all items are reported as visible. */
    enabled?: boolean;
}

/**
 * Measures a row of items against its container and returns how many leading items fit.
 * As many items as fit are kept visible; the caller moves the rest into an overflow menu,
 * reserving room for the overflow trigger when it's needed. Recomputes on container resize.
 *
 * Mirrors the fitting logic in {@link ResponsiveActions} so tab bars and action rows behave
 * the same: fill the available width first, only overflow what genuinely doesn't fit.
 */
export function useResponsiveOverflow(
    keys: string[],
    { gap = 0, reservedWidth = 0, widthBuffer = 0, enabled = true }: ResponsiveOverflowOptions = {},
) {
    const keyString = keys.join(' ');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const itemRefs = useRef<Record<string, HTMLElement | null>>({});
    const widthCache = useRef<Record<string, number>>({});
    const [visibleCount, setVisibleCount] = useState(keys.length);

    const setItemRef = useCallback(
        (key: string) => (el: HTMLElement | null) => {
            itemRefs.current[key] = el;
        },
        [],
    );

    const recompute = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const orderedKeys = keyString ? keyString.split(' ') : [];

        // Cache the natural width of every item that is currently in-flow (visible).
        for (const key of orderedKeys) {
            const el = itemRefs.current[key];
            if (el && el.offsetWidth > 0) {
                widthCache.current[key] = el.offsetWidth;
            }
        }

        const available = container.clientWidth - widthBuffer;

        // Find the largest prefix of items that fits, reserving room for the overflow trigger when some overflow.
        let best = 0;
        for (let k = orderedKeys.length; k >= 0; k--) {
            let width = 0;
            for (let i = 0; i < k; i++) {
                width += (widthCache.current[orderedKeys[i]] ?? 0) + (i > 0 ? gap : 0);
            }
            if (k < orderedKeys.length) {
                width += (k > 0 ? gap : 0) + reservedWidth;
            }
            if (width <= available) {
                best = k;
                break;
            }
        }

        setVisibleCount(best);
    }, [keyString, gap, reservedWidth, widthBuffer]);

    useLayoutEffect(() => {
        if (enabled) {
            recompute();
        }
    }, [recompute, enabled]);

    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => recompute());
        observer.observe(container);
        return () => observer.disconnect();
    }, [recompute, enabled]);

    return {
        containerRef,
        setItemRef,
        visibleCount: enabled ? visibleCount : keys.length,
    };
}
